import { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, Chip, Tooltip, IconButton, FormControlLabel, Switch,
} from '@mui/material';
import { Download, Refresh, ViewColumn } from '@mui/icons-material';
import {
  DataGridPremium, GridColDef, GridRenderCellParams, GridPaginationModel, GridColumnResizeParams, type GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { trackingService, ClickRecord } from '../../services/tracking.service';
import { trafficSourcesService } from '../../services/traffic-sources.service';
import ColumnsDialog from './ColumnsDialog';
import { useColumnPrefs } from '../../hooks/useColumnPrefs';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { downloadGridCsv } from '../../utils/csv-export';
import { hiddenFrom, trafficSourceVarNames } from '../../utils/tracking-helpers';
import { formatLocal, dayRangeParams } from '../../utils/timezone';
import TimeCell from '../../components/TimeCell';
import { COUNTRIES_MAP } from '../../utils/countries';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import DateRangePicker from '../../components/DateRangePicker';
import TimezoneSelect from '../../components/TimezoneSelect';
import TagSelect from '../../components/TagSelect';
import EntityFilterPicker, { FilterEntry } from '../../components/EntityFilterPicker';
import { ENTITY_FILTER_ORDER } from '../../components/entity-fetchers';
import { useFilterParams } from '../../hooks/useFilterParams';

const typeChip = (t: string | null) => {
  const v = t || '-';
  const color = v === 'lp_click' ? 'success' : v === 'lp_view' ? 'info' : v === 'direct' ? 'default' : 'default';
  return <Chip label={v} size="small" color={color as never} sx={{ fontWeight: 500, minWidth: 68 }} />;
};

const mono = (params: GridRenderCellParams) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    {params.value ? (
      <Tooltip title={String(params.value)}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} noWrap>
          {String(params.value)}
        </Typography>
      </Tooltip>
    ) : '-'}
  </Box>
);

/**
 * Individual click log. Every filter lives in the URL, so a report row can deep-link
 * straight to the clicks behind it (and the link stays shareable) — the same reason
 * the conversions page is built this way.
 */
export default function ClicksPage() {
  // Must match the report you drilled in from, or the same rows cover a different window.
  const reportDefaults = useReportDefaults();
  const [dateRange, setDateRange] = useUrlState<[Date | null, Date | null]>(
    'dates', reportDefaults.dateRange, codecs.dateRange,
  );
  const [filters, setFilters] = useUrlState<FilterEntry[]>('filters', [], codecs.json<FilterEntry[]>());
  const [pickedField, setPickedField] = useUrlState('ffield', ENTITY_FILTER_ORDER[0], codecs.str);
  const [tagIds, setTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  const [pagination, setPagination] = useUrlState('page', { page: 0, pageSize: 100 }, codecs.pageModel);
  const { page, pageSize } = pagination;
  const [timezone, setTimezone] = useUrlState('tz', reportDefaults.timezone, codecs.num);
  const [includeBots, setIncludeBots] = useUrlState('bots', false, codecs.bool);
  // Free-form dimension filters (v1-v10, country, device, …) carried in from a report row.
  const [dims, setDims] = useUrlState<Record<string, string>>('dims', {}, codecs.json<Record<string, string>>());

  const { date_from, date_to } = dayRangeParams(dateRange);

  const { filterParams, filtersKey } = useFilterParams(filters);
  const dimsKey = JSON.stringify(dims);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['clicks-list', date_from, date_to, filtersKey, tagIds.join(','), dimsKey, timezone, includeBots, page, pageSize],
    queryFn: () => trackingService.getClicks({
      date_from,
      date_to,
      ...filterParams,
      ...(tagIds.length && { tags: tagIds }),
      // A "(none)" slice is a real filter (empty string), so pass through anything defined.
      ...Object.fromEntries(Object.entries(dims).filter(([, v]) => v !== undefined)),
      timezone,
      include_bots: includeBots,
      limit: pageSize,
      offset: page * pageSize,
    }),
    // Keep the old rows while the next page loads: a grid whose rowCount drops to 0
    // mid-fetch clamps itself back to page 0.
    placeholderData: (prev) => prev,
  });

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  // Show the traffic source's real v1-v10 names when exactly one source is in play.
  const tsFilterValues = filters.filter(f => f.field === 'traffic_source_id').map(f => f.value);
  const activeTsId = tsFilterValues.length === 1 ? tsFilterValues[0] : '';
  const { data: activeTs } = useQuery({
    queryKey: ['clicks-ts-vnames', activeTsId],
    queryFn: () => trafficSourcesService.getById(activeTsId),
    enabled: !!activeTsId,
  });
  const vNames = useMemo(() => trafficSourceVarNames(activeTs || null), [activeTs]);

  const rows = data?.data || [];

  const allColumns: GridColDef[] = useMemo(() => [
    {
      field: 'created_at', headerName: 'Time', width: 165,
      valueFormatter: (v) => (v ? formatLocal(v as string) : '-'),
      renderCell: (p) => <TimeCell value={p.value} tz={timezone} />,
    },
    {
      field: 'click_type', headerName: 'Type', width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{typeChip(p.value)}</Box>
      ),
    },
    { field: 'route_reason', headerName: 'Route Reason', width: 150, valueFormatter: (v) => v || '-' },
    {
      field: 'is_bot', headerName: 'Bot', width: 70,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {Number(p.value) === 1
            ? <Tooltip title={p.row.bot_reason || 'bot'}><Chip label="bot" size="small" color="warning" /></Tooltip>
            : <Typography variant="caption" color="text.secondary">-</Typography>}
        </Box>
      ),
    },
    { field: 'traffic_source_name', headerName: 'Traffic Source', width: 130, valueFormatter: (v) => v || '-' },
    { field: 'offer_name', headerName: 'Offer', width: 180, valueFormatter: (v) => v || '-' },
    { field: 'landing_page_name', headerName: 'Landing Page', width: 180, valueFormatter: (v) => v || '-' },
    {
      field: 'cost', headerName: 'Cost', width: 90, type: 'number',
      valueFormatter: (v) => (v == null ? '-' : `$${Number(v).toFixed(4)}`),
    },
    // The field that exposed the Taboola break — blank here means cost can't attach to this click.
    { field: 'ext_ad_id', headerName: 'Ext Ad ID', width: 150, valueFormatter: (v) => v || '(blank)' },
    {
      field: 'country', headerName: 'Country', width: 110,
      valueFormatter: (v) => (v ? COUNTRIES_MAP[String(v).toUpperCase()] || String(v) : '-'),
    },
    { field: 'region', headerName: 'Region', width: 90, valueFormatter: (v) => v || '-' },
    { field: 'device', headerName: 'Device', width: 100, valueFormatter: (v) => v || '-' },
    { field: 'os', headerName: 'OS', width: 110, valueFormatter: (v) => v || '-' },
    { field: 'browser', headerName: 'Browser', width: 110, valueFormatter: (v) => v || '-' },
    { field: 'lang', headerName: 'Language', width: 90, valueFormatter: (v) => v || '-' },
    { field: 'ip', headerName: 'IP', width: 120, valueFormatter: (v) => v || '-' },
    { field: 'external_id_type', headerName: 'Source', width: 100, valueFormatter: (v) => v || '-' },
    ...Array.from({ length: 10 }, (_, i) => i + 1).map((n): GridColDef => {
      const real = vNames[`v${n}`];
      const slot = `V${n}`;
      return {
        field: `v${n}`,
        headerName: real ? `${slot}: ${real}` : slot,
        width: real ? 150 : 90,
        valueFormatter: (v) => v || '-',
        renderHeader: real
          ? () => <span><span style={{ color: '#9e9e9e', fontWeight: 400 }}>{slot}:</span> {real}</span>
          : undefined,
      };
    }),
    { field: 'click_id', headerName: 'Click ID', width: 190, renderCell: mono },
    { field: 'external_id', headerName: 'External ID', width: 170, renderCell: mono },
    // The raw incoming URL exactly as the traffic source sent it — decoded, because
    // the interesting part is the params, and %7B%7B is unreadable. This is where an
    // unsubstituted macro ({{key1}}) or an unconfigured placeholder (REPLACE_ME) shows.
    {
      field: 'request_url', headerName: 'Request URL (original)', width: 320,
      valueGetter: (v) => {
        if (!v) return '';
        try { return decodeURIComponent(String(v)); } catch { return String(v); }
      },
      renderCell: mono,
    },
    { field: 'destination_url', headerName: 'Destination', width: 220, renderCell: mono },
    { field: 'referer', headerName: 'Referrer', width: 180, renderCell: mono },
    { field: 'user_agent', headerName: 'User Agent', width: 220, renderCell: mono },
    { field: 'decision_path', headerName: 'Decision Path', width: 320, valueFormatter: (v) => v || '-', renderCell: mono },
  ], [vNames, timezone]);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const catalogue = useMemo(
    () => allColumns.map((c) => ({ field: c.field, label: String(c.headerName || c.field), available: true })),
    [allColumns]);
  const catalogueOrder = useMemo(() => catalogue.map((c) => c.field), [catalogue]);
  const { columns: orderedColumns, visibility, save: saveColumns, order: colOrder, hidden: colHidden } =
    useColumnPrefs(allColumns, catalogueOrder, 'clicks');
  const { columns, remember: rememberWidth } = useColumnWidths('clicks', orderedColumns);
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibility[c.field] !== false), [columns, visibility]);

  // Chips for dimension filters that came in via a deep-link, so it's obvious what's applied.
  const dimChips = Object.entries(dims).filter(([, v]) => v !== undefined);

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <EntityFilterPicker
            filters={filters} onFilters={setFilters}
            field={pickedField} onField={setPickedField}
          />
          <TagSelect value={tagIds} onChange={setTagIds} allowCreate={false} label="Tags" placeholder="All" sx={{ minWidth: 170, maxWidth: 280 }} />

          <Box sx={{ flex: 1 }} />

          <FormControlLabel
            control={<Switch size="small" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} />}
            label={<Typography variant="body2">Bots</Typography>}
          />
          <TimezoneSelect value={timezone} onChange={setTimezone} />
          <DateRangePicker value={dateRange} onChange={setDateRange} />

          <Tooltip title="Export CSV">
            <span>
              <IconButton size="small" disabled={!rows.length}
                onClick={() => downloadGridCsv(rows, visibleColumns, 'clicks-export.csv')}>
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Columns">
            <IconButton size="small" onClick={() => setColumnsOpen(true)}><ViewColumn fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()}><Refresh fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        {dimChips.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">From report:</Typography>
            {dimChips.map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                label={`${k.toUpperCase()}: ${v === '' ? '(none)' : v}`}
                onDelete={() => {
                  const next = { ...dims };
                  delete next[k];
                  setDims(next);
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <DataGridPremium
        rows={rows}
        getRowId={(row: ClickRecord) => `${row.click_id}-${row.created_at}-${row.click_type ?? ''}`}
        columns={columns}
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={(m: GridColumnVisibilityModel) => saveColumns(colOrder, hiddenFrom(m))}
        onColumnWidthChange={(params: GridColumnResizeParams) => rememberWidth(params.colDef.field, params.width)}
        loading={isFetching}
        density="compact"
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => setPagination({
          page: model.pageSize !== pageSize ? 0 : model.page,
          pageSize: model.pageSize,
        })}
        pageSizeOptions={[50, 100, 200, 400]}
        disableRowSelectionOnClick
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          height: gridHeight,
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
        }}
      />

      <ColumnsDialog
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        catalogue={catalogue}
        order={colOrder}
        hidden={colHidden}
        onSave={saveColumns}
      />
    </Box>
  );
}
